/**
 * @beenokle/analytics — Lightweight analytics SDK for Beenokle products.
 *
 * Usage:
 *   import { init, track, page, identify } from '@beenokle/analytics';
 *   init({ apiUrl: 'https://accounts.beenokle.ru' });
 *   track('pricing_viewed', { plan: 'yearly' });
 *   page('Pricing');
 *   identify(accessToken);  // after login — links anonymous events to the authenticated user
 *
 * Or as a global embed (UMD):
 *   <script src="https://accounts.beenokle.ru/sdk/beenokle-analytics.umd.js"></script>
 *   <script>
 *     BeenokleAnalytics.init({ apiUrl: 'https://accounts.beenokle.ru' });
 *     BeenokleAnalytics.track('page_viewed');
 *   </script>
 */

// ─── Types ───────────────────────────────────────────────────────────

export interface AnalyticsConfig {
  /** Base URL of the accounts API (e.g. https://accounts.beenokle.ru) */
  apiUrl: string
  /** Optional Bearer token for authenticated tracking */
  token?: string
  /** App identifier (e.g. 'zenwriter', 'dos') */
  appId?: string
  /** Flush interval in ms (default: 5000) */
  flushIntervalMs?: number
  /** Max batch size (default: 20) */
  maxBatchSize?: number
  /** Enable debug logging (default: false) */
  debug?: boolean
  /** Whether optional analytics processing is allowed (default: true for existing integrations). */
  consent?: boolean
}

export interface TrackPayload {
  event: string
  event_type?: 'track' | 'page'
  properties?: Record<string, unknown>
  context?: Record<string, unknown>
  app_id?: string
  session_id?: string
  anonymous_id?: string
  idempotency_key?: string
  timestamp?: string
}

// ─── State ───────────────────────────────────────────────────────────

let config: AnalyticsConfig | null = null
let anonymousId: string | null = null
let sessionId: string | null = null
let lastActivityTs = 0
let queue: QueueEntry[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let consentGranted = true
let lifecycleHandlersAttached = false

const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

interface QueueEntry extends TrackPayload {
  _retries?: number
  _type?: 'track' | 'page'
}

const MAX_RETRIES = 3

// ─── Helpers ─────────────────────────────────────────────────────────

function getOrCreateAnonymousId(): string {
  if (anonymousId) return anonymousId
  try {
    const stored = localStorage.getItem('_ba_anon_id')
    if (stored) {
      anonymousId = stored
      return stored
    }
  } catch {}
  anonymousId = generateId()
  try {
    localStorage.setItem('_ba_anon_id', anonymousId)
  } catch {}
  return anonymousId
}

function generateId(): string {
  // Crypto-random UUID-like string
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback
  const arr = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr)
  } else {
    for (let i = 0; i < 16; i++) arr[i] = Math.floor(Math.random() * 256)
  }
  const hex = Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function generateIdempotencyKey(event: string): string {
  // Deterministic-ish key based on event + timestamp + random suffix
  const ts = Date.now().toString(36)
  const rand = Math.random().toString(36).slice(2, 8)
  return `${event}:${ts}:${rand}`
}

/**
 * One visit is one session: it survives a reload and a move between pages of a site that loads
 * the SDK afresh, and ends after 30 minutes without activity or when the tab closes. The id was
 * written to sessionStorage but never read back, so every page load started a new session and a
 * visit of five pages counted as five one-page, bounced sessions. Stored as `<id>.<last activity>`.
 */
function getOrCreateSessionId(): string {
  const now = Date.now()
  if (!sessionId) {
    try {
      const stored = sessionStorage.getItem('_ba_sid') || ''
      const dot = stored.lastIndexOf('.')
      const id = dot > 0 ? stored.slice(0, dot) : ''
      const at = Number(stored.slice(dot + 1))
      if (id && Number.isFinite(at) && now - at < SESSION_TIMEOUT_MS) {
        sessionId = id
        lastActivityTs = at
      }
    } catch {}
  }
  if (!sessionId || now - lastActivityTs >= SESSION_TIMEOUT_MS) sessionId = generateId()
  lastActivityTs = now
  try {
    sessionStorage.setItem('_ba_sid', `${sessionId}.${now}`)
  } catch {}
  return sessionId
}

function extractUtmParams(): Record<string, string> {
  const utm: Record<string, string> = {}
  if (typeof location === 'undefined') return utm
  try {
    const params = new URLSearchParams(location.search)
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = params.get(key)
      if (v) utm[key] = v
    }
  } catch {}
  return utm
}

/**
 * Analytics URLs intentionally exclude query strings and fragments. Authentication callbacks,
 * password resets and signed links commonly carry bearer credentials there. UTM fields are kept
 * separately by an explicit allowlist in `extractUtmParams`.
 */
export function analyticsUrlWithoutCredentials(value: string | undefined): string | undefined {
  if (value === undefined) return undefined
  const query = value.indexOf('?')
  const fragment = value.indexOf('#')
  const cutAt = [query, fragment].filter((index) => index >= 0).sort((a, b) => a - b)[0]
  return cutAt === undefined ? value : value.slice(0, cutAt)
}

function buildAutoContext(): Record<string, unknown> {
  const ctx: Record<string, unknown> = {}
  if (typeof navigator !== 'undefined') {
    ctx.user_agent = navigator.userAgent
    ctx.language = navigator.language
  }
  if (typeof screen !== 'undefined') {
    ctx.screen_width = screen.width
    ctx.screen_height = screen.height
  }
  if (typeof window !== 'undefined') {
    ctx.viewport_width = window.innerWidth
    ctx.viewport_height = window.innerHeight
  }
  if (typeof location !== 'undefined') {
    ctx.page_url = analyticsUrlWithoutCredentials(location.href)
    ctx.page_path = location.pathname
    ctx.referrer =
      typeof document !== 'undefined'
        ? analyticsUrlWithoutCredentials(document.referrer || undefined)
        : undefined
  }
  return ctx
}

function debugLog(...args: unknown[]) {
  if (config?.debug) {
    console.log('[BeenokleAnalytics]', ...args)
  }
}

// ─── Core ────────────────────────────────────────────────────────────

/**
 * Initialize the analytics SDK. Must be called before any tracking.
 */
export function init(cfg: AnalyticsConfig): void {
  config = cfg
  consentGranted = cfg.consent !== false
  if (consentGranted) {
    getOrCreateAnonymousId()
    startFlushTimer()
  } else {
    clearAnalyticsState()
    stopFlushTimer()
  }
  debugLog('initialized', { apiUrl: cfg.apiUrl, appId: cfg.appId, anonymousId })
}

/**
 * Apply the person's current analytics preference.
 *
 * Withdrawing consent is a hard boundary: queued events and locally persisted analytics identifiers
 * are removed, and no network request can be made until consent is granted again.
 */
export function setConsent(granted: boolean): void {
  if (consentGranted === granted) return
  consentGranted = granted
  if (!granted) {
    clearAnalyticsState()
    stopFlushTimer()
    debugLog('consent withdrawn')
    return
  }

  if (!config) return
  getOrCreateAnonymousId()
  startFlushTimer()
  page()
  if (config.token) identify()
  debugLog('consent granted')
}

/**
 * Set or update the Bearer token (e.g. after login).
 */
export function setToken(token: string): void {
  if (config) config.token = token
}

/**
 * Track a named event.
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!config) {
    console.warn('[BeenokleAnalytics] not initialized')
    return
  }
  if (!consentGranted) return

  const payload: TrackPayload = {
    event,
    properties,
    context: buildAutoContext(),
    app_id: config.appId,
    session_id: getOrCreateSessionId(),
    anonymous_id: getOrCreateAnonymousId(),
    idempotency_key: generateIdempotencyKey(event),
    timestamp: new Date().toISOString(),
  }

  queue.push(payload)
  debugLog('queued track', event, properties)

  // Flush immediately if batch is full
  const maxBatch = config.maxBatchSize ?? 20
  if (queue.length >= maxBatch) {
    flush()
  }
}

/**
 * Track a page view. Routed through the batch queue for efficiency.
 */
export function page(name?: string, properties?: Record<string, unknown>): void {
  if (!config) {
    console.warn('[BeenokleAnalytics] not initialized')
    return
  }
  if (!consentGranted) return

  const url = typeof location !== 'undefined' ? analyticsUrlWithoutCredentials(location.href) : undefined
  const referrer =
    typeof document !== 'undefined'
      ? analyticsUrlWithoutCredentials(document.referrer || undefined)
      : undefined
  const title = typeof document !== 'undefined' ? document.title : undefined

  const utm = extractUtmParams()
  const payload: QueueEntry = {
    event: name || 'page_view',
    event_type: 'page',
    properties: { ...properties, url, referrer, title, ...utm },
    context: buildAutoContext(),
    app_id: config.appId,
    session_id: getOrCreateSessionId(),
    anonymous_id: getOrCreateAnonymousId(),
    idempotency_key: generateIdempotencyKey('page_view'),
    timestamp: new Date().toISOString(),
  }

  queue.push(payload)
  debugLog('queued page', name || url)

  const maxBatch = config.maxBatchSize ?? 20
  if (queue.length >= maxBatch) flush()
}

/**
 * Identify the current user (link anonymous events to authenticated user).
 * Call this after login when you have a Bearer token.
 */
export function identify(token?: string): void {
  if (!config) {
    console.warn('[BeenokleAnalytics] not initialized')
    return
  }
  if (!consentGranted) return

  if (token) config.token = token
  if (!config.token) {
    debugLog('identify skipped: no token')
    return
  }

  const anonId = getOrCreateAnonymousId()
  debugLog('identify', anonId)

  fetch(`${config.apiUrl}/api/v1/events/identify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.token}`,
    },
    body: JSON.stringify({ anonymous_id: anonId, ...(config.appId ? { app_id: config.appId } : {}) }),
    keepalive: true,
  }).catch((err) => debugLog('identify failed', err))
}

/**
 * Flush all queued events to the server.
 */
export function flush(): void {
  if (!config || !consentGranted || queue.length === 0) return

  const batch = queue.splice(0)
  debugLog('flushing', batch.length, 'events')

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (config.token) headers['Authorization'] = `Bearer ${config.token}`

  // Strip internal fields before sending
  const cleanBatch = batch.map(({ _retries, ...rest }) => rest)

  // Re-queue with a retry budget — drop events that exceeded it.
  const retry = () => {
    for (const entry of batch) {
      const retries = (entry._retries ?? 0) + 1
      if (retries <= MAX_RETRIES) {
        entry._retries = retries
        queue.push(entry)
      } else {
        debugLog('dropped event after max retries', entry.event)
      }
    }
  }

  fetch(`${config.apiUrl}/api/v1/events/batch`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ events: cleanBatch }),
    keepalive: true,
  })
    .then((response) => {
      // A response is not a success: a refused batch used to vanish as if it had been stored.
      // Overloaded or failing (429, 5xx) is worth another try; a refusal (4xx) would be refused again.
      if (response.ok) return
      if (response.status === 429 || response.status >= 500) retry()
      else debugLog('batch refused', response.status)
    })
    .catch((err) => {
      debugLog('flush failed', err)
      retry()
    })
}

/**
 * Reset the SDK state (e.g. on logout). Generates a new anonymous ID.
 */
export function reset(): void {
  flush()
  anonymousId = null
  sessionId = null
  lastActivityTs = 0
  try {
    localStorage.removeItem('_ba_anon_id')
  } catch {}
  try {
    sessionStorage.removeItem('_ba_sid')
  } catch {}
  if (consentGranted) getOrCreateAnonymousId()
  if (config) config.token = undefined
  debugLog('reset')
}

// ─── Internal ────────────────────────────────────────────────────────

function startFlushTimer(): void {
  if (flushTimer) return
  const interval = config?.flushIntervalMs ?? 5000
  flushTimer = setInterval(flush, interval)

  // Flush on page unload
  if (typeof window !== 'undefined' && !lifecycleHandlersAttached) {
    lifecycleHandlersAttached = true
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
    window.addEventListener('pagehide', flush)
  }
}

function stopFlushTimer(): void {
  if (!flushTimer) return
  clearInterval(flushTimer)
  flushTimer = null
}

function clearAnalyticsState(): void {
  queue = []
  anonymousId = null
  sessionId = null
  lastActivityTs = 0
  try {
    localStorage.removeItem('_ba_anon_id')
  } catch {}
  try {
    sessionStorage.removeItem('_ba_sid')
  } catch {}
}

// ─── Auto Page Tracking (for SPAs) ──────────────────────────────────

let autoPageEnabled = false

/**
 * Enable automatic page view tracking on route changes (SPA-aware).
 * Hooks into popstate and patches history.pushState/replaceState.
 */
export function enableAutoPageTracking(): void {
  if (autoPageEnabled || typeof window === 'undefined') return
  autoPageEnabled = true

  let lastUrl = location.href

  function onUrlChange() {
    const newUrl = location.href
    if (newUrl !== lastUrl) {
      lastUrl = newUrl
      page()
    }
  }

  window.addEventListener('popstate', onUrlChange)

  // Patch pushState / replaceState to detect SPA navigation
  const origPush = history.pushState
  const origReplace = history.replaceState
  history.pushState = function (...args) {
    origPush.apply(this, args)
    onUrlChange()
  }
  history.replaceState = function (...args) {
    origReplace.apply(this, args)
    onUrlChange()
  }

  // Track initial page view
  page()
  debugLog('auto page tracking enabled')
}

// ─── Auto-Init (UMD embed via global config) ────────────────────────

if (typeof window !== 'undefined') {
  const globalConfig = (window as any).BeenokleAnalyticsConfig as AnalyticsConfig | undefined
  if (globalConfig?.apiUrl) {
    init(globalConfig)
    enableAutoPageTracking()
  }
}
